import { Box, Grid, MenuItem } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** CONSTANTS */
import { colors } from 'constants/theme'

/** COMPONENT */
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import BranchFilterSearch from 'new-components/select/branch-filter-search'
import FilterSelect from 'component/Select/FilterSelect'
import { CardSalesProps, handleChangeFilterSelect, renderFilterSelect } from '../dashboard'

/** UTILS || SLICE */
import { getSecureItem, numberFormat } from 'utils/app.utils'

/** API */
import { BranchInterface } from 'api/master/clinic.api'

/** STYLE */
import DashboardApi, { IFBastSellOperativeType, IFSumeryFilter } from 'api/dashboard.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import { KEY_STORAGE } from 'constants/common'
import * as UseStyled from '../useStyled'

export default function OperativeType(props: CardSalesProps) {
    const { t } = useTranslation()

    const bg = colors.dashboard.operativeType

    const customBar = {
        maxBarThickness: 40,
        borderRadius: 6,
    }

    // data
    const [labels, setLabels]: any = useState([])
    const [labelProducts, setLabelProducts]: any = useState([])
    const [data, setData]: any = useState({
        labels: [],
        datasets: [
            {
                label: 'a',
                data: [],
                backgroundColor: bg,
                hoverBackgroundColor: bg,
                ...customBar
            },
        ]
    })

    const [loadFirst, setLoadFirst] = useState(false);
    const dashboardMain = getSecureItem(KEY_STORAGE.DASHBOARD)
    const dashboardCurrent = dashboardMain ? JSON.parse(dashboardMain) : {}
    const [optionOperativeType, setOptionOperativeType] = useState<any>([])
    const [selectOperativeType, setSelectOperativeType] = useState<(string | number)[]>(dashboardCurrent?.operativeType?.type || ['ALL'])

    const [branches, setBranches] = useState<BranchInterface[]>([])
    const [branch, setBranch] = useState<(string | number)[]>(dashboardCurrent?.operativeType?.branch || ['ALL'])
    const [rangeDate, setRangeDate] = useState(dashboardCurrent?.operativeType?.rangeDate || [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')])
    const [minMax, setMinMax] = useState([0, 0]);

    const handleChangeSelectBranch = (e: any) => setBranch(handleChangeFilterSelect(e))

    const handleChangeSelectOperativeType = (e: any) => setSelectOperativeType(handleChangeFilterSelect(e))

    const loadData = useCallback(async () => {
        const allBranchIds = (branches || []).map((b) => b.branchId).join(',')
        const condition: IFSumeryFilter = { dateStart: rangeDate[0], dateEnd: rangeDate[1], branchId: branch[0] === 'ALL' ? allBranchIds : branch.toString(), type: selectOperativeType[0] === 'ALL' ? '' : selectOperativeType.toString() }
        const res = await DashboardApi.findSellOperativeType(condition)

        if (res.status === 200) {
            const summeris: IFBastSellOperativeType[] = res.data;
            const dataLabels: string[] = summeris.map(x => x.branchName);

            const allPayments = _.flatMap(summeris, 'payments');
            const uniqueOperativeTypes = _.uniqBy(allPayments, 'operativeTypeId');

            const dataProducts = uniqueOperativeTypes.map(op => {
                const operativeTypeInfo = optionOperativeType.find((opt: any) => opt.itemTypeId === op.operativeTypeId);
                const color = operativeTypeInfo?.color || op.color || _.sample(bg);
                return {
                    ...customBar,
                    data: Array(summeris.length).fill(0),
                    backgroundColor: color,
                    operativeTypeId: op.operativeTypeId,
                    operativeTypeName: op.operativeTypeName,
                };
            });

            const dataLabelProducts = uniqueOperativeTypes.map(op => ({
                data: Array(summeris.length).fill(null),
                operativeTypeName: op.operativeTypeName,
            }));

            summeris.forEach((branchData, branchIndex) => {
                branchData.payments.forEach(payment => {
                    const datasetIndex = dataProducts.findIndex(ds => ds.operativeTypeId === payment.operativeTypeId);
                    if (datasetIndex !== -1) {
                        dataProducts[datasetIndex].data[branchIndex] = payment.amountPaid;
                        dataLabelProducts[datasetIndex].data[branchIndex] = payment.operativeTypeName;
                    }
                });
            });

            setLabels(dataLabels);
            setLabelProducts(dataLabelProducts);
            setData({
                labels: dataLabels,
                datasets: dataProducts,
            });
        }
    }, [branch, rangeDate, selectOperativeType, optionOperativeType, branches])

    useEffect(() => {
        loadData()
    }, [loadData])

    useEffect(() => {
        if (!loadFirst) {
            const timer = setTimeout(() => {
                setLoadFirst(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [props.rangeDate, props.branches]);

    useEffect(() => {
        setBranches(props.branches || [])
    }, [props.branches])

    useEffect(() => {
        loadFirst && setRangeDate(props.rangeDate || [])
    }, [props.rangeDate])

    useEffect(() => {
        loadFirst && setBranch(props.branch || [])
    }, [props.branch])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,branch", branch)
    }, [branch])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,rangeDate", rangeDate)
    }, [rangeDate])

    useEffect(() => {
        props?.handleChangeDashboard && props?.handleChangeDashboard("operativeType,type", selectOperativeType)
    }, [selectOperativeType])

    const loadOperativeType = async (search?: string) => {
        const res = await OperativeTypeApi.findAll({ pageLimit: 30, search: search, status: 'ACTIVE', export: 1 })
        if (res.status === 200) {
            const newData = res.data.map((value: any) => {
                return { itemTypeName: value.operativeTypeName, itemTypeId: value.operativeTypeId, color: value?.color }
            })
            setOptionOperativeType(newData)
        }
    }

    useEffect(() => {
        loadOperativeType()
    }, [])

    const options = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        if (!labelProducts) return numberFormat(context.raw)
                        if (!labelProducts[context.datasetIndex].data[context.dataIndex]) return numberFormat(context.raw)
                        return ` ${labelProducts[context.datasetIndex].data[context.dataIndex]}: ${numberFormat(context.raw)}`
                    },
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    drawBorder: false,
                    drawOnChartArea: false,
                    drawTicks: false,
                    color: colors.lightGray
                },
                ticks: {
                    color: colors.textExtraLightGray,
                    fontSize: labels.length > 6 ? 12 : 16,
                    padding: 10,
                    autoSkip: false,
                    maxRotation: labels.length > 6 ? 45 : 0,
                    minRotation: 0,
                    callback: function (val: any, index: number) {
                        const label = t('DASHBOARD.FILTER.BRANCH') + labels[val]
                        const maxLength = 10;


                        const words = label.split(' ');
                        const lines = [];
                        let currentLine = '';

                        for (const word of words) {
                            if (currentLine.length + word.length <= maxLength) {
                                currentLine += word + ' ';
                            } else {
                                lines.push(currentLine.trim());
                                currentLine = word + ' ';
                            }
                        }

                        if (currentLine.trim().length > 0) {
                            lines.push(currentLine.trim());
                        }

                        return lines;
                    },
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: colors.lightGray
                },
                ticks: {
                    color: colors.textExtraLightGray,
                    fontSize: 16,
                    borderRadius: 8,
                    callback: function (val: any, index: number) {
                        return numberFormat(Number(val).toFixed(0))
                    },
                }
            }
        }
    }

    const renderData = useCallback(() => {
        const newOption = selectOperativeType[0] === 'ALL' ? optionOperativeType : optionOperativeType.filter((item: any) => selectOperativeType.includes(item?.itemTypeId))
        return (
            <Box display={'flex'} alignItems={'center'} flexWrap={'wrap'} gap={'12px'}>
                {newOption.map((item: any, index: number) => {
                    return (
                        <Box key={index} display={'flex'} alignItems={'center'} gap={'8px'} flex={'1 1 200px'} maxWidth={'200px'}>
                            <Box minWidth={'20px'} minHeight={'20px'} borderRadius={'4px'} sx={{ backgroundColor: item?.color }} />
                            <Box fontSize={'13px'}>
                                {item?.itemTypeName}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        )
    }, [data])

    return (
        <UseStyled.Card className='d-flex flex-column'>
            <Box className='pt-3 px-3 px-xl-4' sx={{ borderBottom: `1px solid ${colors.lightGray}`, paddingBottom: '12px' }}>
                <Grid container spacing={1} alignItems={'center'}>
                    <Grid item xs={12} className={'w-100'}>
                        <UseStyled.Title type='title' sx={{ flex: 'none' }}>{t('DASHBOARD.OPERATIVE_TYPE')}</UseStyled.Title>
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <BranchFilterSearch
                            branches={branches}
                            value={branch}
                            onchange={handleChangeSelectBranch}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <FilterSelect
                            multiple
                            style={{ height: '40px' }}
                            label={t('')}
                            labelId="label-selectOperativeType"
                            selectId="select-selectOperativeType"
                            onchange={handleChangeSelectOperativeType}
                            renderValue={() => `${t('DASHBOARD.OPERATIVE_TYPE')}: ${renderFilterSelect('operativeType', optionOperativeType, selectOperativeType) === 'ALL' ? t('STATUS.ALL') : renderFilterSelect('operativeType', optionOperativeType, selectOperativeType)}`}
                            value={selectOperativeType}
                            options={[
                                <MenuItem
                                    key="ALL"
                                    value="ALL"
                                    sx={{
                                        '&.MuiMenuItem-root.Mui-selected': {
                                            pointerEvents: _.includes(selectOperativeType, 'ALL') ? `none` : 'auto',
                                            backgroundColor: _.includes(selectOperativeType, 'ALL') ? `${colors.themeSecondColor06} !important` : 'transparent',
                                            color: _.includes(selectOperativeType, 'ALL') ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                        }
                                    }}
                                >
                                    {t('STATUS.ALL')}
                                </MenuItem>,
                                _.map(optionOperativeType, (val: any, index: number) => {
                                    return (
                                        <MenuItem
                                            key={index}
                                            value={val.itemTypeId}
                                            sx={{
                                                '&.MuiMenuItem-root.Mui-selected': {
                                                    backgroundColor: _.includes(selectOperativeType, val.itemTypeId) ? `${colors.themeSecondColor06} !important` : 'transparent',
                                                    color: _.includes(selectOperativeType, val.itemTypeId) ? `${colors.themeSecondColor} !important` : `${colors.textPrimary}`
                                                }
                                            }}
                                        >
                                            {val.itemTypeName}
                                        </MenuItem>
                                    )
                                })
                            ]}
                            formControlClass={'not-max-width'}
                            classesOption="style-select-doctors"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} className={'w-100'}>
                        <InputSecondNewRangePicker
                            inputHeight={40}
                            value={rangeDate}
                            onchange={(val) => {
                                if (_.isEmpty(val)) setRangeDate(emptyRangePicker)
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                            }}
                            allowClear
                            onClear={() => setRangeDate(emptyRangePicker)}
                        />
                    </Grid>
                </Grid>
            </Box>
            <Box className='p-3 px-xl-4 h-100'>
                <Bar options={options} data={data} />
                <Box>
                    {renderData()}
                </Box>
            </Box>
        </UseStyled.Card>
    )
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
)